/**
 * Load every image a document references, before anything is compiled.
 *
 * A DOCX embeds images by value, so the bytes have to be in hand to build one —
 * and fetching them is asynchronous, while compiling to IR is not. Keeping the
 * compiler synchronous is worth a pre-pass: it makes compilation a pure
 * function of the document plus this map, which is what lets the IR be compared,
 * cached and snapshotted.
 *
 * Sources are deduplicated, so the same image used ten times is fetched once
 * and embedded once.
 */

import {
  isNativeVisualProps,
  type VisualProps,
} from '@json-to-office/shared-docx';
import { imageIntegrityDefect } from '@json-to-office/shared/images/node';
import type { ComponentDefinition } from '../types';
import {
  getImageBuffer,
  readImageDimensions,
  resolveImageSource,
} from '../utils/imageUtils';

export interface LoadedImage {
  bytes: Buffer;
  /** From the HTTP response, when the source was fetched over the network. */
  contentType?: string;
  /** Pixel size as stored, when it could be read. */
  intrinsic?: { width: number; height: number };
}

/**
 * Images by source string.
 *
 * A source that could not be loaded is absent rather than recorded as an
 * error: the compiler reports it against the component's own path, which is
 * more use than an error attached to a URL. Why it failed is kept beside the
 * images, so that report can say — and so a source that was tried and failed
 * can be told from one the walk never reached.
 */
export interface ImageResources extends ReadonlyMap<string, LoadedImage> {
  /** What each source that did not load threw. */
  readonly failures?: ReadonlyMap<string, unknown>;
}

/** Load every image source in `components`, following nested content. */
export async function loadImageResources(
  components: Iterable<ComponentDefinition | undefined>
): Promise<ImageResources> {
  const sources = new Set<string>();
  for (const component of components) collectSources(component, sources);

  const loaded = new Map<string, LoadedImage>();
  const failures = new Map<string, unknown>();
  await Promise.all(
    [...sources].map(async (source) => {
      try {
        const result = await getImageBuffer(source);
        // The header alone sizes a damaged image fine, and Word then shows
        // "The picture can't be displayed". A source whose bytes would not
        // decode fails here, like one that could not be read at all.
        const defect = imageIntegrityDefect(result.buffer);
        if (defect) throw new Error(defect);
        // Probed from the bytes just loaded, never by loading the source a
        // second time: a URL fetched twice is twice the traffic, and a URL
        // whose response changes would embed one image and size it from
        // another (#267).
        const intrinsic = readIntrinsic(result.buffer, source);
        loaded.set(source, {
          bytes: result.buffer,
          ...(result.contentType ? { contentType: result.contentType } : {}),
          ...(intrinsic ? { intrinsic } : {}),
        });
      } catch (error) {
        // Left out of the map; the compiler reports it against the component.
        failures.set(source, error);
      }
    })
  );
  return Object.assign(loaded, { failures });
}

function readIntrinsic(
  bytes: Buffer,
  source: string
): { width: number; height: number } | undefined {
  try {
    return readImageDimensions(bytes, source);
  } catch {
    // An unreadable header is not a failure to load: the image still embeds,
    // it just cannot contribute an aspect ratio.
    return undefined;
  }
}

/**
 * Every image source under one component.
 *
 * Images hide in more places than the component tree suggests — inside table
 * cells, inside header and footer content — so this walks the shapes that can
 * hold one rather than only a component's `children`.
 */
function collectSources(
  component: ComponentDefinition | undefined,
  out: Set<string>
): void {
  if (!component || typeof component !== 'object') return;

  if (component.name === 'image') {
    const source = resolveImageSource(
      (component.props ?? {}) as {
        svg?: string;
        base64?: string;
        path?: string;
      }
    );
    if (source) out.add(source);
  }

  const children = (component as { children?: ComponentDefinition[] }).children;
  for (const child of children ?? []) collectSources(child, out);

  const props = (component.props ?? {}) as Record<string, unknown>;

  // A native `visual` survives desugaring, so its pictures are still nested
  // inside it when this runs — and they embed by value like any other image.
  // A raster visual is already an `image` by now and has no `elements` left.
  if (
    component.name === 'visual' &&
    isNativeVisualProps(props as VisualProps)
  ) {
    collectNativeVisualSources(props, out);
  }
  for (const key of ['header', 'footer'] as const) {
    const part = props[key];
    if (Array.isArray(part)) {
      for (const child of part)
        collectSources(child as ComponentDefinition, out);
    }
  }

  const columns = props.columns;
  if (Array.isArray(columns)) {
    for (const column of columns as Record<string, unknown>[]) {
      const header = column?.header as { content?: unknown } | undefined;
      collectCellSource(header?.content, out);
      const cells = column?.cells;
      if (Array.isArray(cells)) {
        for (const cell of cells as { content?: unknown }[]) {
          collectCellSource(cell?.content, out);
        }
      }
    }
  }
}

/**
 * Every image a native visual draws: its canvas background, and each `image`
 * element on it.
 *
 * The elements are the native content model rather than docx components, so
 * they are read directly rather than recursed into — nothing else in there
 * can hold an image.
 */
function collectNativeVisualSources(
  props: Record<string, unknown>,
  out: Set<string>
): void {
  const canvas = props.canvas as
    | { background?: { image?: { path?: string; base64?: string } } }
    | undefined;
  const background = canvas?.background?.image;
  if (background) {
    const source = resolveImageSource(background);
    if (source) out.add(source);
  }

  const elements = props.elements;
  if (!Array.isArray(elements)) return;
  for (const element of elements as Record<string, unknown>[]) {
    if (!element || element.name !== 'image') continue;
    const source = resolveImageSource(
      (element.props ?? {}) as { svg?: string; base64?: string; path?: string }
    );
    if (source) out.add(source);
  }
}

function collectCellSource(content: unknown, out: Set<string>): void {
  if (content && typeof content === 'object') {
    collectSources(content as ComponentDefinition, out);
  }
}
